import { motion, AnimatePresence } from 'framer-motion'
import { X, TrendingUp, Target, Award, BarChart3, PieChart, LineChart, AlertCircle, CheckCircle, Sparkles } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface HabitsScoreDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const HabitsScoreDetailsModal = ({ isOpen, onClose }: HabitsScoreDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600/20 to-teal-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <Award className="h-8 w-8 text-blue-400" />
                      {t('habitsScore.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('habitsScore.modal.subtitle')}</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                {/* Introduction */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-blue-500/10 to-teal-500/10 rounded-2xl p-6 border border-blue-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('habitsScore.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">
                          {t('habitsScore.modal.intro')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Score Calculation - Main Component */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <BarChart3 className="h-6 w-6 text-blue-400" />
                    {t('habitsScore.modal.calculation')}
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Component 1: Saving Rate */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-green-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-green-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('habitsScore.modal.savingRate.title')}</h4>
                          <span className="text-sm text-green-400 font-semibold">40 {t('habitsScore.modal.points')}</span>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">
                        {t('habitsScore.modal.savingRate.description')}
                      </p>
                      
                      {/* Progress Example */}
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{t('habitsScore.modal.savingRate.target')}</span>
                            <span className="text-green-400 font-semibold">20%+</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 w-full rounded-full" />
                          </div>
                        </div>
                        
                        <div className="bg-green-500/10 rounded-lg p-3 text-xs">
                          <code className="text-green-300">
                            {t('habitsScore.modal.savingRate.formula')}
                          </code>
                        </div>
                      </div>
                    </motion.div>

                    {/* Component 2: Budget Adherence */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-blue-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                          <Target className="h-6 w-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('habitsScore.modal.budgetAdherence.title')}</h4>
                          <span className="text-sm text-blue-400 font-semibold">30 {t('habitsScore.modal.points')}</span>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">
                        {t('habitsScore.modal.budgetAdherence.description')}
                      </p>
                      
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-400">{t('habitsScore.modal.budgetAdherence.optimal')}</span>
                            <span className="text-blue-400 font-semibold">70-90%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-blue-400 to-cyan-500 w-[85%] rounded-full" />
                          </div>
                        </div>
                        
                        <div className="bg-blue-500/10 rounded-lg p-3 text-xs space-y-1">
                          <div className="flex items-center gap-2 text-green-400">
                            <CheckCircle className="h-3 w-3" />
                            <span>{t('habitsScore.modal.budgetAdherence.good')}</span>
                          </div>
                          <div className="flex items-center gap-2 text-orange-400">
                            <AlertCircle className="h-3 w-3" />
                            <span>{t('habitsScore.modal.budgetAdherence.overspend')}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Component 3: Spending Consistency */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-purple-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-500/20 rounded-lg">
                          <BarChart3 className="h-6 w-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('habitsScore.modal.spendingConsistency.title')}</h4>
                          <span className="text-sm text-purple-400 font-semibold">20 {t('habitsScore.modal.points')}</span>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">
                        {t('habitsScore.modal.spendingConsistency.description')}
                      </p>
                      
                      <div className="bg-purple-500/10 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-400">{t('habitsScore.modal.spendingConsistency.stability')}</span>
                          <span className="text-purple-400 font-semibold text-sm">15/20</span>
                        </div>
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-purple-400 to-fuchsia-500 w-[75%] rounded-full" />
                        </div>
                      </div>
                    </motion.div>

                    {/* Component 4: Financial Health */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-amber-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-amber-500/20 rounded-lg">
                          <Award className="h-6 w-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-white">{t('habitsScore.modal.financialHealth.title')}</h4>
                          <span className="text-sm text-amber-400 font-semibold">10 {t('habitsScore.modal.points')}</span>
                        </div>
                      </div>
                      <p className="text-gray-300 text-sm mb-4">
                        {t('habitsScore.modal.financialHealth.description')}
                      </p>
                      
                      <div className="bg-amber-500/10 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          <span className="text-white text-sm font-medium">{t('habitsScore.modal.financialHealth.positive')}</span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Score Distribution Pie Chart Visual */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <PieChart className="h-6 w-6 text-teal-400" />
                    {t('habitsScore.modal.distribution')}
                  </h3>

                  <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Pie Chart Visualization */}
                      <div className="flex items-center justify-center">
                        <div className="relative w-64 h-64">
                          {/* Pie segments */}
                          <svg viewBox="0 0 100 100" className="transform -rotate-90">
                            {/* Saving Rate - 40% */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="rgb(34, 197, 94)"
                              strokeWidth="20"
                              strokeDasharray="100.53 251.33"
                              strokeDashoffset="0"
                            />
                            {/* Budget Adherence - 30% */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="rgb(59, 130, 246)"
                              strokeWidth="20"
                              strokeDasharray="75.4 251.33"
                              strokeDashoffset="-100.53"
                            />
                            {/* Spending Consistency - 20% */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="rgb(168, 85, 247)"
                              strokeWidth="20"
                              strokeDasharray="50.27 251.33"
                              strokeDashoffset="-175.93"
                            />
                            {/* Financial Health - 10% */}
                            <circle
                              cx="50"
                              cy="50"
                              r="40"
                              fill="none"
                              stroke="rgb(245, 158, 11)"
                              strokeWidth="20"
                              strokeDasharray="25.13 251.33"
                              strokeDashoffset="-226.20"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                                100
                              </div>
                              <div className="text-xs text-gray-400">{t('habitsScore.modal.totalPoints')}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Legend */}
                      <div className="flex flex-col justify-center space-y-4">
                        {[
                          { color: 'bg-green-500', label: t('habitsScore.modal.savingRate.title'), value: '40%' },
                          { color: 'bg-blue-500', label: t('habitsScore.modal.budgetAdherence.title'), value: '30%' },
                          { color: 'bg-purple-500', label: t('habitsScore.modal.spendingConsistency.title'), value: '20%' },
                          { color: 'bg-amber-500', label: t('habitsScore.modal.financialHealth.title'), value: '10%' },
                        ].map((item, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-4 h-4 rounded ${item.color}`} />
                              <span className="text-white text-sm">{item.label}</span>
                            </div>
                            <span className="text-gray-400 font-semibold">{item.value}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Score Tiers */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Award className="h-6 w-6 text-yellow-400" />
                    {t('habitsScore.modal.tiers')}
                  </h3>

                  <div className="grid md:grid-cols-4 gap-4">
                    {[
                      { tier: t('habitsScore.modal.excellent'), range: '80-100', color: 'from-green-500 to-emerald-600', emoji: '🌟' },
                      { tier: t('habitsScore.modal.good'), range: '60-79', color: 'from-blue-500 to-cyan-600', emoji: '💪' },
                      { tier: t('habitsScore.modal.fair'), range: '40-59', color: 'from-orange-500 to-amber-600', emoji: '🚀' },
                      { tier: t('habitsScore.modal.needsWork'), range: '0-39', color: 'from-red-500 to-rose-600', emoji: '💡' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                        className={`bg-gradient-to-br ${item.color} rounded-xl p-4 text-center`}
                      >
                        <div className="text-3xl mb-2">{item.emoji}</div>
                        <div className="text-white font-bold mb-1">{item.tier}</div>
                        <div className="text-white/80 text-sm">{item.range}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Mobile App Features */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <LineChart className="h-6 w-6 text-cyan-400" />
                    {t('habitsScore.modal.features')}
                  </h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Overview Tab */}
                    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 border border-blue-500/30">
                      <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center mb-4">
                        <BarChart3 className="h-6 w-6 text-blue-400" />
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">{t('habitsScore.modal.overview')}</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.overviewFeature1')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.overviewFeature2')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.overviewFeature3')}</span>
                        </li>
                      </ul>
                    </div>

                    {/* Breakdown Tab */}
                    <div className="bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 rounded-2xl p-6 border border-purple-500/30">
                      <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center mb-4">
                        <PieChart className="h-6 w-6 text-purple-400" />
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">{t('habitsScore.modal.breakdown')}</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.breakdownFeature1')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.breakdownFeature2')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.breakdownFeature3')}</span>
                        </li>
                      </ul>
                    </div>

                    {/* History Tab */}
                    <div className="bg-gradient-to-br from-teal-500/20 to-emerald-500/20 rounded-2xl p-6 border border-teal-500/30">
                      <div className="w-12 h-12 bg-teal-500/30 rounded-xl flex items-center justify-center mb-4">
                        <LineChart className="h-6 w-6 text-teal-400" />
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">{t('habitsScore.modal.history')}</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.historyFeature1')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.historyFeature2')}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{t('habitsScore.modal.historyFeature3')}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-blue-600/20 to-teal-600/20 rounded-2xl p-8 border border-blue-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('habitsScore.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">
                    {t('habitsScore.modal.ctaDescription')}
                  </p>
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all"
                  >
                    {t('habitsScore.modal.gotIt')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default HabitsScoreDetailsModal
